/*******************************************************************************
 * NSFCheck */
/**
 * @ngdoc   run
 * @module  csm.books.nsf
 * @name    csm.books.nsf.run:NSFCheck
 * @description
 * Creates a data object constructor by setting default values and functions on
 * the prototype. The data will be initialized from the `nsf` and `payment`.
 *******************************************************************************/
/* @ngInject */ export function runNsfCheckData(Data, Tools) {
  /*****************************************************************************
   * Constructor
   *****************************************************************************/
  Data.NSFCheck = function (nsf, payment) {
    /*-----------------------------------------*/
    /* Create the nsfCheck and checkPayment    */
    /* objects from the provided data objects. */
    /*-----------------------------------------*/
    var nsfCheck = Object.assign(
      Object.create(Data.NSFCheck.prototype),
      nsf || {}
    );

    /*-----------------------------------------------------------------*/
    /* If this NSF is not new, but we have no payment, that means the  */
    /* NSF was created for an unrecorded check. We need to migrate the */
    /* bank and amount properties from the checkItem (less the bank    */
    /* charge) that was saved so that it can be displayed properly.    */
    /*-----------------------------------------------------------------*/
    if (!nsfCheck.isNew && !payment)
      payment = {
        bankAcctID: nsfCheck.checkItem.accountID,
        /***
         * If the bank charge is set, then we need to subtract it from the checkItem amount. This is because the check item amount is the check payment
         * amount + the bank charge amount. If the bank charge is not set, then the checkItem amount is the check payment amount.
         */
        checkAmount: nsfCheck.bankCharge.amount
          ? Tools.roundMoney(
              nsfCheck.checkItem.amount - nsfCheck.bankCharge.amount
            )
          : nsfCheck.checkItem.amount
      };

    nsfCheck.checkPayment = new Data.CheckPayment(payment);

    /*-----------------------------------------*/
    /* Initialize local properties with data   */
    /* from the server if this is from a load. */
    /*-----------------------------------------*/
    if (!nsfCheck.isNew) {
      /*----------------------------------------------------------------------------*/
      /* If there are any reversals, remap the array to set the reversal amount to  */
      /* the actual amount reversed. We do this because when we load a check for a  */
      /* new NSF, the Amount for the reversal is the original amount, but when      */
      /* loaded from an existing NSF, the amount is the selected amount to reverse. */
      /*                                                                            */
      /* This should be fixed to use a single object on the client side, and set    */
      /* the appropriate values on the server side.                                 */
      /*----------------------------------------------------------------------------*/
      if (nsf.reversals)
        nsf.reversals.forEach(function (reversal) {
          for (var i = 0; i < nsfCheck.checkPayment.reversals.length; i++)
            if (
              nsfCheck.checkPayment.reversals[i].ledgerItemID ==
              reversal.referenceItemID
            )
              nsfCheck.checkPayment.reversals[i].reversal = reversal.amount;
        });
    }

    return nsfCheck;
  };

  /*****************************************************************************
   * Prototype
   *****************************************************************************/
  Data.NSFCheck.prototype = {
    /*----------------------------------------------------*/
    /* These properties should not be accessed outside    */
    /* of this instance. They will be set by the getters. */
    /*----------------------------------------------------*/
    mBankCharge: null,
    mStudentFunds: null,
    mRevenue: null,

    id: 0,
    entryDate: null,
    cleared: false,
    paid: false,
    memo: null,
    fine: null,

    get bankAcctID() {
      return this.bankCharge.accountID;
    },
    get bankCharge() {
      return this.mBankCharge || (this.mBankCharge = {});
    },
    get chargeAmount() {
      return this.bankCharge.amount || 0;
    },
    get checkRemaining() {
      return Tools.roundMoney(
        this.checkPayment.checkAmount + this.totalReversed
      );
    },
    get isNew() {
      return !this.id || this.id < 0;
    },
    get isNewUnrecorded() {
      return this.isNew && !this.checkPayment.id;
    },
    get revenue() {
      return this.mRevenue || (this.mRevenue = {});
    },
    get revenueAcctID() {
      return this.revenue.accountID;
    },
    get studentFunds() {
      return this.mStudentFunds || (this.mStudentFunds = {});
    },
    get totalRemaining() {
      return (this.revenue.amount = Tools.roundMoney(
        this.checkRemaining - this.onAccount
      ));
    },
    get totalReversed() {
      return this.checkPayment.totalReversed || 0;
    },

    /***************************************************************************
     * onAccount */
    /**
     * Get the most amount of funds that the student can put towards the amount
     * of the NSF. If this value was set by the user, then we will use that value
     * instead.
     ***************************************************************************/
    get onAccount() {
      if (this.studentFunds.amount) return this.studentFunds.amount;

      var checkAmount = this.checkPayment.checkAmount;
      var funds = this.checkPayment.funds;
      var amount = Math.max(0, Math.min(checkAmount, funds));

      if (amount > 0) this.studentFunds.amount = amount;

      return amount;
    },

    set bankCharge(value) {
      this.mBankCharge = value;
    },
    set bankAcctID(value) {
      this.bankCharge.accountID = value;
    },
    set chargeAmount(value) {
      this.bankCharge.amount = value;
    },
    set onAccount(value) {
      this.studentFunds.amount = Math.max(
        0,
        Math.min(
          value,
          this.checkPayment.checkAmount,
          this.checkPayment.funds
        )
      );
    },
    set revenueAcctID(value) {
      this.revenue.accountID = value;
    },
    set studentFunds(value) {
      this.mStudentFunds = value;
    },
    set revenue(value) {
      this.mRevenue = value;
    },

    /***************************************************************************
     * toJSON */
    /**
     * Converts all accessors to properties.
     ***************************************************************************/
    toJSON: function () {
      var result = {};
      for (var key in this) result[key] = this[key];

      return result;
    }
  };
};
